/**
 * Shared constants for MLB teams and other static data.
 *
 * This is the single source of truth for frontend team data.
 * Uses uppercase 3-letter canonical codes (KCR, SDP, SFG, TBR).
 */

export interface Team {
  code: string; // Canonical 3-letter uppercase code (e.g., 'KCR')
  name: string; // Full team name (e.g., 'Kansas City Royals')
  shortName: string; // Short display name (e.g., 'Royals')
  division: string; // Division (e.g., 'AL Central')
  displayCode?: string; // UI abbreviation when different from code (e.g., 'KC' for KCR)
}

/**
 * Division order for consistent iteration.
 */
export const DIVISION_ORDER = [
  'AL East',
  'AL Central',
  'AL West',
  'NL East',
  'NL Central',
  'NL West',
] as const;

/**
 * All 30 MLB teams sorted alphabetically by city/name.
 * Uses canonical 3-letter uppercase codes (KCR, SDP, SFG, TBR).
 */
export const ALL_TEAMS: Team[] = [
  { code: 'ARI', name: 'Arizona Diamondbacks', shortName: 'D-backs', division: 'NL West' },
  { code: 'ATL', name: 'Atlanta Braves', shortName: 'Braves', division: 'NL East' },
  { code: 'BAL', name: 'Baltimore Orioles', shortName: 'Orioles', division: 'AL East' },
  { code: 'BOS', name: 'Boston Red Sox', shortName: 'Red Sox', division: 'AL East' },
  { code: 'CHC', name: 'Chicago Cubs', shortName: 'Cubs', division: 'NL Central' },
  { code: 'CWS', name: 'Chicago White Sox', shortName: 'White Sox', division: 'AL Central' },
  { code: 'CIN', name: 'Cincinnati Reds', shortName: 'Reds', division: 'NL Central' },
  { code: 'CLE', name: 'Cleveland Guardians', shortName: 'Guardians', division: 'AL Central' },
  { code: 'COL', name: 'Colorado Rockies', shortName: 'Rockies', division: 'NL West' },
  { code: 'DET', name: 'Detroit Tigers', shortName: 'Tigers', division: 'AL Central' },
  { code: 'HOU', name: 'Houston Astros', shortName: 'Astros', division: 'AL West' },
  {
    code: 'KCR',
    name: 'Kansas City Royals',
    shortName: 'Royals',
    division: 'AL Central',
    displayCode: 'KC',
  },
  { code: 'LAA', name: 'Los Angeles Angels', shortName: 'Angels', division: 'AL West' },
  { code: 'LAD', name: 'Los Angeles Dodgers', shortName: 'Dodgers', division: 'NL West' },
  { code: 'MIA', name: 'Miami Marlins', shortName: 'Marlins', division: 'NL East' },
  { code: 'MIL', name: 'Milwaukee Brewers', shortName: 'Brewers', division: 'NL Central' },
  { code: 'MIN', name: 'Minnesota Twins', shortName: 'Twins', division: 'AL Central' },
  { code: 'NYM', name: 'New York Mets', shortName: 'Mets', division: 'NL East' },
  { code: 'NYY', name: 'New York Yankees', shortName: 'Yankees', division: 'AL East' },
  { code: 'OAK', name: 'Athletics', shortName: 'Athletics', division: 'AL West' },
  { code: 'PHI', name: 'Philadelphia Phillies', shortName: 'Phillies', division: 'NL East' },
  { code: 'PIT', name: 'Pittsburgh Pirates', shortName: 'Pirates', division: 'NL Central' },
  {
    code: 'SDP',
    name: 'San Diego Padres',
    shortName: 'Padres',
    division: 'NL West',
    displayCode: 'SD',
  },
  {
    code: 'SFG',
    name: 'San Francisco Giants',
    shortName: 'Giants',
    division: 'NL West',
    displayCode: 'SF',
  },
  { code: 'SEA', name: 'Seattle Mariners', shortName: 'Mariners', division: 'AL West' },
  { code: 'STL', name: 'St. Louis Cardinals', shortName: 'Cardinals', division: 'NL Central' },
  {
    code: 'TBR',
    name: 'Tampa Bay Rays',
    shortName: 'Rays',
    division: 'AL East',
    displayCode: 'TB',
  },
  { code: 'TEX', name: 'Texas Rangers', shortName: 'Rangers', division: 'AL West' },
  { code: 'TOR', name: 'Toronto Blue Jays', shortName: 'Blue Jays', division: 'AL East' },
  { code: 'WSH', name: 'Washington Nationals', shortName: 'Nationals', division: 'NL East' },
];

/**
 * All 30 MLB teams with lowercase codes for backend API calls.
 * Derived from ALL_TEAMS.
 */
export const ALL_TEAMS_LOWERCASE = ALL_TEAMS.map((team) => ({
  code: team.code.toLowerCase(),
  name: team.name,
}));

/**
 * Teams grouped by division. Derived from ALL_TEAMS.
 */
export const TEAMS_BY_DIVISION: Record<string, Team[]> = Object.fromEntries(
  DIVISION_ORDER.map((div) => [div, ALL_TEAMS.filter((t) => t.division === div)])
);

/**
 * Alias map: legacy/short codes -> canonical 3-letter uppercase code.
 * Used by lookup functions to resolve old-format codes.
 */
const CODE_ALIASES: Record<string, string> = {
  KC: 'KCR',
  SD: 'SDP',
  SF: 'SFG',
  TB: 'TBR',
};

// Alternate team codes from legacy data (backfill scripts used different abbreviations)
const ALTERNATE_TEAM_CODES: Record<string, string> = {
  wsn: 'Washington Nationals',
  ana: 'Los Angeles Angels',
  ath: 'Athletics',
};

// WBC team names (duplicated from constants/wbc.ts to avoid circular imports)
const _WBC_NAMES: Record<string, string> = {
  aus: 'Australia',
  bra: 'Brazil',
  can: 'Canada',
  tpe: 'Chinese Taipei',
  col: 'Colombia',
  cub: 'Cuba',
  cze: 'Czechia',
  dom: 'Dominican Republic',
  gbr: 'Great Britain',
  isr: 'Israel',
  ita: 'Italy',
  jpn: 'Japan',
  kor: 'Korea',
  mex: 'Mexico',
  ned: 'Netherlands',
  nca: 'Nicaragua',
  pan: 'Panama',
  pur: 'Puerto Rico',
  usa: 'United States',
  ven: 'Venezuela',
};

/**
 * Resolve a team code to its canonical uppercase form.
 * Handles aliases (KC -> KCR) and case normalization.
 */
function resolveCode(code: string): string {
  const upper = code.toUpperCase();
  return CODE_ALIASES[upper] || upper;
}

/**
 * Find a team by code (case-insensitive, handles aliases).
 */
function findTeam(code: string): Team | undefined {
  const canonical = resolveCode(code);
  return ALL_TEAMS.find((t) => t.code === canonical);
}

/**
 * Get the display abbreviation for a team code.
 * Returns the short form (e.g., "KC") if available, otherwise the canonical code.
 */
export function getDisplayCode(code: string): string {
  const team = findTeam(code);
  return team?.displayCode || resolveCode(code);
}

/**
 * Get team name from team code (case-insensitive).
 * Returns shortName (e.g., "Yankees") if available, otherwise full name.
 * Returns the code itself if not found.
 *
 * @param code Team code (e.g., "NYY", "nyy", "KCR", "kc")
 * @returns Team short name or the code if not found
 */
export function getTeamName(code: string | null | undefined): string | null {
  if (!code) return null;
  if (code === 'FA') return 'Free Agent';

  const team = findTeam(code);
  if (team) return team.shortName || team.name;

  // Legacy alternate codes (wsn -> Nationals, etc.)
  const altName = ALTERNATE_TEAM_CODES[code.toLowerCase()];
  if (altName) return altName.split(' ').pop() || altName;

  return code.toUpperCase();
}

/**
 * Get full team name from team code (case-insensitive).
 * Returns full name (e.g., "Baltimore Orioles") unlike getTeamName which returns short names.
 *
 * @param code Team code (e.g., "NYY", "nyy", "bal", "kc")
 * @returns Full team name or the uppercased code if not found
 */
export function getTeamFullName(code: string | null | undefined): string | null {
  if (!code) return null;
  if (code === 'FA') return 'Free Agent';

  const team = findTeam(code);
  if (team) return team.name;

  // Legacy alternate codes (wsn -> Washington Nationals, etc.)
  const altName = ALTERNATE_TEAM_CODES[code.toLowerCase()];
  if (altName) return altName;

  // WBC teams (lowercase codes like jpn, usa, dom)
  const wbcName = _WBC_NAMES[code.toLowerCase()];
  if (wbcName) return wbcName;

  return code.toUpperCase();
}

/**
 * Stats available for trend charts.
 */
export const TREND_STATS = [
  // Summary
  { value: 'RS_g', label: 'RS/g', category: 'Summary' },
  { value: 'RA_g', label: 'RA/g', category: 'Summary' },
  { value: 'bRAA', label: 'bRAA', category: 'Summary' },
  { value: 'bsRAA', label: 'bsRAA', category: 'Summary' },
  { value: 'dRAA', label: 'dRAA', category: 'Summary' },
  { value: 'pRAR', label: 'pRAR', category: 'Summary' },
  { value: 'WAR', label: 'WAR', category: 'Summary' },
  // Offense
  { value: 'OBP', label: 'OBP', category: 'Offense' },
  { value: 'SLG', label: 'SLG', category: 'Offense' },
  { value: 'H_g', label: 'H/g', category: 'Offense' },
  { value: 'BB_g', label: 'BB/g', category: 'Offense' },
  { value: 'HR_g', label: 'HR/g', category: 'Offense' },
  { value: 'wRC_plus', label: 'wRC+', category: 'Offense' },
  { value: 'wOBA', label: 'wOBA', category: 'Offense' },
  // Pitching
  { value: 'K_9', label: 'K/9', category: 'Pitching' },
  { value: 'BB_9', label: 'BB/9', category: 'Pitching' },
  { value: 'HR_9', label: 'HR/9', category: 'Pitching' },
  { value: 'BABIP', label: 'BABIP', category: 'Pitching' },
  { value: 'ERA', label: 'ERA', category: 'Pitching' },
  { value: 'FIP', label: 'FIP', category: 'Pitching' },
  { value: 'xFIP', label: 'xFIP', category: 'Pitching' },
] as const;

/**
 * Position types for filtering.
 */
export const POSITION_TYPES = ['Pitcher', 'Catcher', 'Infielder', 'Outfielder', 'DH'] as const;

/**
 * Manager filter options.
 */
export const MANAGER_FILTERS = {
  minGames: 5, // Minimum games to be considered a real manager (not fill-in)
} as const;

/**
 * Get the current MLB season year.
 *
 * Returns the previous year if we're before April (spring training / off-season).
 * This matches the backend's get_current_season() logic.
 *
 * @returns The current MLB season year
 */
export function getCurrentMlbSeason(): number {
  const now = new Date();
  const month = now.getMonth() + 1; // getMonth() is 0-indexed
  const year = now.getFullYear();

  // Before April, use previous year as "current season"
  if (month < 4) {
    return year - 1;
  }
  return year;
}

/**
 * MLB venue name -> city mapping for display as "Venue, City".
 */
export const VENUE_CITIES: Record<string, string> = {
  'American Family Field': 'Milwaukee',
  'Angel Stadium': 'Anaheim',
  'Busch Stadium': 'St. Louis',
  'Chase Field': 'Phoenix',
  'Citi Field': 'New York',
  'Citizens Bank Park': 'Philadelphia',
  'Comerica Park': 'Detroit',
  'Coors Field': 'Denver',
  'Dodger Stadium': 'Los Angeles',
  'Fenway Park': 'Boston',
  'Globe Life Field': 'Arlington',
  'Great American Ball Park': 'Cincinnati',
  'Guaranteed Rate Field': 'Chicago',
  'Kauffman Stadium': 'Kansas City',
  'loanDepot park': 'Miami',
  'Minute Maid Park': 'Houston',
  'Nationals Park': 'Washington',
  'Oakland Coliseum': 'Oakland',
  'Oracle Park': 'San Francisco',
  'Oriole Park at Camden Yards': 'Baltimore',
  'Petco Park': 'San Diego',
  'PNC Park': 'Pittsburgh',
  'Progressive Field': 'Cleveland',
  'Rogers Centre': 'Toronto',
  'T-Mobile Park': 'Seattle',
  'Target Field': 'Minneapolis',
  'Tropicana Field': 'St. Petersburg',
  'Truist Park': 'Atlanta',
  'Wrigley Field': 'Chicago',
  'Yankee Stadium': 'New York',
  // WBC venues
  'Daikin Park': 'Osaka',
  'Tokyo Dome': 'Tokyo',
  'Hiram Bithorn Stadium': 'San Juan',
};

/**
 * Format a venue name with its city for display.
 * Returns "Venue, City" if city is known, otherwise just the venue name.
 */
export function formatVenue(venue: string | null | undefined): string | null {
  if (!venue) return null;
  const city = VENUE_CITIES[venue];
  return city ? `${venue}, ${city}` : venue;
}

/**
 * All known MLB park names for filter dropdowns, sorted alphabetically.
 */
export const MLB_VENUES = Object.entries(VENUE_CITIES)
  .map(([name, city]) => `${name}, ${city}`)
  .sort();
